import { format, parseISO } from "date-fns";
import { ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { AnimatedNumber } from "./AnimatedNumber";
import { CAP_FILL } from "./balance-tab-variants";
import { BalanceTracks } from "./BalanceTracks";
import type { CaptainBalanceState } from "../balance-state";
import { BALANCE_FILL, CaptainTokensIcon } from "../constants";
import { useTopUpSpent } from "../hooks/useTopUpSpent";
import type { CaptainBillingAccount } from "../types";
import { balanceTracks } from "../utils";
import { HEADLINE_COMPACT_FROM, useShortTokenFormat, useTokenFormat } from "./tiles/useTokenFormat";

import { ROUTES } from "@/constants/routes";
import { cn } from "@/lib/utils/cn";
import { getDateFnsLocale } from "@/utils/dateUtils";
import { useValueFormatter } from "@/utils/useValueFormatter";

const TOKEN_MANAGEMENT_PATH = `/${ROUTES.tokenManagement}`;

type BalanceBreakdownProps = {
  account: CaptainBillingAccount;
  available: number;
  refillDate: string;
  state: CaptainBalanceState;
};

export function BalanceBreakdown({ account, available, refillDate, state }: BalanceBreakdownProps) {
  const { t, i18n } = useTranslation();
  const { format: formatValue } = useValueFormatter();
  const hasCap = account.monthlyCapTokens > 0;
  const headlineTokens = useShortTokenFormat(HEADLINE_COMPACT_FROM);
  const { spent: spentFromTopUp } = useTopUpSpent(account);
  const tracks = balanceTracks(account, spentFromTopUp);

  return (
    <>
      <div className="flex flex-col gap-3 p-4">
        <div className="flex items-baseline justify-between gap-2 text-xs">
          <span className="flex items-center gap-1.5 text-sm font-semibold">
            <CaptainTokensIcon className="size-4 text-primary" aria-hidden />
            {t("captain.topbar.title", { defaultValue: "Tokens" })}
          </span>
          <span className="text-muted-foreground">
            {format(parseISO(account.month), "LLLL yyyy", { locale: getDateFnsLocale(i18n.language) })}
          </span>
        </div>
        <div className="flex items-baseline gap-2">
          <AnimatedNumber
            value={available}
            format={headlineTokens}
            title={formatValue(available, "decimal")}
            className="text-3xl font-semibold tracking-tight"
          />
          <span className="text-sm text-muted-foreground">{t("captain.topbar.available", { defaultValue: "available" })}</span>
        </div>
        <BalanceTracks tracks={tracks} capClass={CAP_FILL[state]} />
        <dl className="flex flex-col gap-1.5 text-sm">
          {hasCap && (
            <BalanceRow
              swatch={CAP_FILL[state]}
              label={t("captain.topbar.capLeft", { defaultValue: "Subscription cap left" })}
              value={(format) =>
                t("captain.topbar.capLeftValue", {
                  defaultValue: "{{left}} of {{cap}}",
                  left: format(Math.max(0, account.capLeftTokens)),
                  cap: format(account.monthlyCapTokens),
                })
              }
            />
          )}
          <BalanceRow
            swatch={BALANCE_FILL.topUp}
            label={t("captain.topbar.topUp", { defaultValue: "Top-up balance" })}
            value={(format) =>
              spentFromTopUp > 0
                ? t("captain.topbar.capLeftValue", {
                  defaultValue: "{{left}} of {{cap}}",
                  left: format(account.topUpBalanceTokens),
                  cap: format(account.topUpBalanceTokens + spentFromTopUp),
                })
                : format(account.topUpBalanceTokens)
            }
          />
        </dl>
        <StateHint state={state} hasCap={hasCap} refillDate={refillDate} />
      </div>
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 border-t border-border px-4 py-3 text-xs">
        <span className="text-muted-foreground">
          {hasCap
            ? t("captain.topbar.capRefills", { defaultValue: "Cap refills on {{date}}", date: refillDate })
            : t("captain.topbar.noCap", { defaultValue: "No subscription cap" })}
        </span>
        <Link
          to={TOKEN_MANAGEMENT_PATH}
          className="inline-flex shrink-0 items-center gap-1 font-semibold text-primary underline-offset-4 hover:underline"
        >
          {t("captain.topbar.viewBilling", { defaultValue: "Token Management" })}
          <ArrowRight className="size-3.5" aria-hidden />
        </Link>
      </div>
    </>
  );
}

type TokenFormat = (tokens: number) => string;

function BalanceRow({ swatch, label, value }: { swatch: string; label: string; value: (format: TokenFormat) => string }) {
  const formatTokens = useTokenFormat();
  const shortTokens = useShortTokenFormat();
  return (
    <div className="flex items-center justify-between gap-4">
      <dt className="flex items-center gap-2 whitespace-nowrap text-muted-foreground">
        <span aria-hidden className={cn("size-2 rounded-sm", swatch)} />
        {label}
      </dt>
      <dd className="whitespace-nowrap font-medium tabular-nums" title={value(formatTokens)}>
        {value(shortTokens)}
      </dd>
    </div>
  );
}

function StateHint({ state, hasCap, refillDate }: { state: CaptainBalanceState; hasCap: boolean; refillDate: string }) {
  const { t } = useTranslation();

  if (state === "low")
    return (
      <p className="text-xs [color:var(--badge-warning-text)]">
        {t("captain.topbar.lowHint", { defaultValue: "Running low. Captain pauses when the balance reaches 0." })}
      </p>
    );
  if (state !== "usedUp") return null;
  return (
    <p className="text-xs [color:var(--badge-error-text)]">
      {hasCap
        ? t("captain.topbar.pausedUntil", { defaultValue: "Captain is paused until {{date}}.", date: refillDate })
        : t("captain.topbar.askForTopUp", { defaultValue: "Captain is paused. Ask your administrator for a top-up." })}
    </p>
  );
}
